import styles from "./DatasetSnapshots.module.scss";
import { DatasetSnapshotMetadata } from "../../../../vl-api/vlSchemas";
import Moment from "react-moment";
import React from "react";
import {
  CloneIcon,
  DeleteIcon,
  PencilIcon,
  RestoreIcon,
  SnapshotIcon,
} from "./Icons";
import { CopyOutlined, MoreOutlined } from "@ant-design/icons";
import VLPopover from "../../../uikit/VLPopover";
import TextWithTooltip from "../../TextWithTooltip";
import { useVisibility } from "../../../../hooks/useVisibility";
import { SnapshotModal } from "./SnapshotModal";
import { useSnapshotItem } from "./useSnapshotItem";
import {
  DATASET_SNAPSHOTS,
  DATASET_SNAPSHOTS_RESTORE,
} from "../../../../helpers/constants/filters";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { useUserPermission } from "../../../../hooks/useUserPermission";
import VLTooltip from "../../../uikit/VLTooltip";

export const SnapshotItem = ({
  snapshot,
  datasetId,
  onClose,
}: {
  onClose: () => void;
  datasetId: string;
  snapshot: DatasetSnapshotMetadata;
}) => {
  const {
    handleRemoveClick,
    handleRestoreClick,
    handleCloneClick,
    handleCopyClick,
  } = useSnapshotItem(datasetId, snapshot.snapshot_id, onClose);
  const modal = useVisibility();
  const popover = useVisibility();
  const isEnabled = useFeatureFlagService(DATASET_SNAPSHOTS).enabled;
  const restoreAndCloneBehavior = useFeatureFlagService(
    DATASET_SNAPSHOTS_RESTORE,
  );
  const canRestoreSnapshot = useUserPermission("can_restore_snapshot");
  const canCloneSnapshot = useUserPermission("can_clone_snapshot");

  const onCopyClick = () => {
    handleCopyClick();
    popover.close();
  };

  return (
    <div className={styles.snapshot}>
      <div className={styles.title}>
        <TextWithTooltip
          className={styles.name}
          text={snapshot.snapshot_name}
        />
        {snapshot.is_current && (
          <div className={styles.currentVersion}>Current Version</div>
        )}
        {snapshot.is_restoring && (
          <div className={styles.restortingVersion}>Restoring</div>
        )}
        <VLPopover
          placement="bottomRight"
          arrow={false}
          open={popover.isOpen}
          onOpenChange={(visible) =>
            visible ? popover.open() : popover.close()
          }
          content={
            <div className={styles.menu}>
              <div className={styles.menuItem} onClick={modal.open}>
                <PencilIcon /> Edit
              </div>
              {!snapshot.is_restoring && canCloneSnapshot && (
                <VLTooltip title={restoreAndCloneBehavior.reason}>
                  <div
                    className={`${styles.menuItem} ${!restoreAndCloneBehavior.enabled ? styles.disabled : ""}`}
                    onClick={
                      !restoreAndCloneBehavior.enabled
                        ? undefined
                        : handleCloneClick
                    }
                  >
                    <CloneIcon /> Clone
                  </div>
                </VLTooltip>
              )}
              <div className={styles.menuItem} onClick={onCopyClick}>
                <CopyOutlined /> Copy Snapshot ID
              </div>
              {!snapshot.is_current && canRestoreSnapshot && (
                <VLTooltip title={restoreAndCloneBehavior.reason}>
                  <div
                    className={`${styles.menuItem} ${!restoreAndCloneBehavior.enabled ? styles.disabled : ""}`}
                    onClick={
                      !restoreAndCloneBehavior.enabled
                        ? undefined
                        : handleRestoreClick
                    }
                  >
                    <RestoreIcon />
                    Restore
                  </div>
                </VLTooltip>
              )}
              {!snapshot.is_current && isEnabled && (
                <div
                  className={styles.menuItem}
                  onClick={() => handleRemoveClick(snapshot.snapshot_name)}
                >
                  <DeleteIcon /> Remove
                </div>
              )}
            </div>
          }
          trigger={"click"}
        >
          <MoreOutlined className={styles.menuIcon} />
        </VLPopover>
      </div>
      <div className={styles.description}>
        <div>{snapshot.description}</div>
      </div>
      <div className={styles.details}>
        <div className={styles.createdAt}>
          <SnapshotIcon />
          <Moment format="M/D/YY, HH:mm" local={true} utc={true}>
            {snapshot.created_at}
          </Moment>
        </div>
        <div>Items: {snapshot.n_images + snapshot.n_videos}</div>
        <div>Created by: {snapshot.created_by}</div>
      </div>
      <SnapshotModal datasetId={datasetId} {...modal} snapshot={snapshot} />
    </div>
  );
};
